import { ApiError, createMember, listMembers, reassignMemberVehicle } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { MemberActiveMembership, MemberResponse } from '@jq4race/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import {
  ArrowLeftRight,
  CheckCircle2,
  Plus,
  TriangleAlert,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { AccessDeniedState } from '../../../../shared/errors/AccessDeniedState';
import { isForbiddenError } from '../../../../shared/errors/isForbiddenError';
import { RestrictedMeta } from '../../../../shared/errors/RestrictedMeta';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { useAutoDismissFeedback } from '../../../../shared/hooks/useAutoDismissFeedback';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { EnrollMemberDialog } from '../components/EnrollMemberDialog';
import { MemberForm } from '../components/MemberForm';
import { ReassignVehicleDialog } from '../components/ReassignVehicleDialog';

export function MembersPage() {
  const { t } = useTranslation('memberships');
  const [members, setMembers] = useState<MemberResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // See EventsPage.tsx for the confirmed production incident this guards
  // against: a 403 (role changed mid-session) must not render like "no
  // members", and its banner must not auto-dismiss.
  const [accessDenied, setAccessDenied] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [enrollTarget, setEnrollTarget] = useState<MemberResponse | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);
    try {
      const res = await listMembers();
      setMembers(res.members);
    } catch (e) {
      setAccessDenied(isForbiddenError(e));
      setError(translateApiError(e, t, { fallback: t('membersPage.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useAutoDismissFeedback({
    error,
    success,
    isErrorPersistent: accessDenied,
    onDismissError: () => setError(null),
    onDismissSuccess: () => setSuccess(null),
  });

  async function handleCreate(values: Parameters<typeof createMember>[0]) {
    try {
      await createMember(values);
      setSuccess(t('membersPage.createSuccess'));
      setCreateOpen(false);
      void reload();
    } catch (e) {
      if (e instanceof ApiError)
        throw new LocalizedError(
          translateApiError(e, t, { fallback: t('membersPage.createError') }),
        );
      throw e;
    }
  }

  async function handleReassign(plate: string, toMemberId: string) {
    try {
      await reassignMemberVehicle({ plate, toMemberId });
      setSuccess(t('membersPage.reassignSuccess', { plate }));
      void reload();
    } catch (e) {
      if (e instanceof ApiError)
        throw new LocalizedError(
          translateApiError(e, t, { fallback: t('membersPage.reassignError') }),
        );
      throw e;
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('membersPage.eyebrow')}
        title={t('membersPage.title')}
        description={t('membersPage.description')}
        meta={
          <p className="text-[13px] text-jq-graphite">
            <RestrictedMeta loading={loading && members.length === 0} accessDenied={accessDenied}>
              <span className="font-medium tabular-nums text-jq-charcoal">{members.length}</span>{' '}
              {t('membersPage.count', { count: members.length })}
            </RestrictedMeta>
          </p>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setReassignOpen(true)}>
              <ArrowLeftRight className="h-4 w-4" strokeWidth={1.75} />
              {t('membersPage.reassignVehicle')}
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.75} />
              {t('membersPage.newMember')}
            </Button>
          </div>
        }
      />

      <FeedbackBanner tone="error" message={error} onDismiss={() => setError(null)} />
      <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {accessDenied ? (
            <AccessDeniedState />
          ) : (
            <MembersTable members={members} loading={loading} onEnroll={setEnrollTarget} />
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={(next) => (!next ? setCreateOpen(false) : undefined)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('membersPage.newMemberDialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="mt-6">
            <MemberForm onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} />
          </div>
        </DialogContent>
      </Dialog>

      <ReassignVehicleDialog
        open={reassignOpen}
        members={members}
        onConfirm={handleReassign}
        onCancel={() => setReassignOpen(false)}
      />

      <EnrollMemberDialog member={enrollTarget} onCancel={() => setEnrollTarget(null)} />
    </div>
  );
}

// ── Table ──────────────────────────────────────────────────────────────────

interface MembersTableProps {
  members: MemberResponse[];
  loading: boolean;
  onEnroll: (member: MemberResponse) => void;
}

function MembersTable({ members, loading, onEnroll }: MembersTableProps) {
  const { t } = useTranslation('memberships');
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('membersPage.table.name')}</TableHead>
          <TableHead>{t('membersPage.table.email')}</TableHead>
          <TableHead>{t('membersPage.table.phone')}</TableHead>
          <TableHead>{t('membersPage.table.plates')}</TableHead>
          <TableHead>{t('membersPage.table.plan')}</TableHead>
          <TableHead className="w-[72px] text-right">{t('membersPage.table.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && members.length === 0 ? (
          <SkeletonRows />
        ) : members.length === 0 ? (
          <TableEmpty colSpan={6}>
            <EmptyState />
          </TableEmpty>
        ) : (
          members.map((member) => (
            <TableRow key={member.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{member.name}</span>
              </TableCell>
              <TableCell className="text-jq-graphite">{member.email}</TableCell>
              <TableCell className="text-jq-graphite">{member.phone ?? '—'}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1.5">
                  {member.vehicles.map((v) => (
                    <Badge key={v.id} variant="secondary">
                      {v.plate}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <PlanCell membership={member.activeMembership} />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => onEnroll(member)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded text-jq-graphite transition hover:bg-jq-surface-2 hover:text-jq-navy"
                    aria-label={t('membersPage.table.enroll', { name: member.name })}
                  >
                    <UserPlus className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

// Non-terminal membership statuses → i18n key + tone. `canceled` never
// reaches here (the backend reports it as `activeMembership: null`).
const MEMBERSHIP_STATUS_META: Record<
  MemberActiveMembership['status'],
  { key: string; variant: 'success' | 'info' | 'warning' | 'danger' }
> = {
  active: { key: 'active', variant: 'success' },
  pending_activation: { key: 'pending', variant: 'info' },
  past_due: { key: 'pastDue', variant: 'warning' },
  suspended: { key: 'suspended', variant: 'danger' },
};

function PlanCell({ membership }: { membership: MemberActiveMembership | null }) {
  const { t } = useTranslation('memberships');
  if (!membership) {
    return <span className="text-jq-graphite">—</span>;
  }
  const meta = MEMBERSHIP_STATUS_META[membership.status];
  return (
    <div className="flex items-center gap-2">
      <span className="font-medium text-jq-charcoal">{membership.planName}</span>
      <Badge variant={meta.variant}>{t(`membersPage.status.${meta.key}`)}</Badge>
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 6 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('memberships');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Users className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('membersPage.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('membersPage.empty.description')}</p>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('memberships');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
